/**
 * ModuleDrilldownDrawer — L2 (group) → L3 (line item) drilldown for the
 * Engineering / Procurement / Construction analytics panels, with a Cognara
 * narrative at whichever level the user is looking at.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, ChevronRight, Sparkles, AlertTriangle, Wrench } from 'lucide-react';
import api from '@/lib/api';
import { usePanelTheme } from './panelTheme';

const LABEL = {
  engineering: 'Engineering · DCI', procurement: 'Procurement · MCI',
  construction: 'Fabrication & Construction', milestones: 'Project Milestones',
  contractors: 'Contractor Performance', schedule: 'Schedule S-Curve · WBS',
  cost: 'Cost & EVM · CBS', equipment: 'Equipment · Register & Utilisation',
};

export const ModuleDrilldownDrawer = ({ projectId, module, onClose }) => {
  const T = usePanelTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openGroup, setOpenGroup] = useState(null);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');

  useEffect(() => {
    if (!projectId || !module) return;
    let alive = true;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/overview/drilldown/${module}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, module]);

  const askCognara = async (group) => {
    setInsightLoading(true); setInsight(null); setProgress(4);
    // Cognara's deep analysis takes 20-90s; show honest staged progress that
    // creeps towards 95% and only completes when the response actually lands.
    const STAGES = [
      [10, 'Reading L2 groups…'], [25, 'Scanning L3 line items…'],
      [42, 'Measuring slip and float…'], [58, 'Running delay analysis…'],
      [72, 'Assessing downstream impact…'], [86, 'Deriving root cause…'],
      [95, 'Composing the brief…'],
    ];
    let i = 0;
    const timer = setInterval(() => {
      if (i < STAGES.length) { setProgress(STAGES[i][0]); setStage(STAGES[i][1]); i += 1; }
    }, 2600);
    try {
      const r = await api.post(`/v1/projects/${projectId}/overview/insight`, { module, group: group || null });
      setInsight(r.data);
    } catch {
      setInsight({ status: 'unavailable', message: 'Cognara could not be reached.' });
    } finally {
      clearInterval(timer);
      setProgress(100); setStage('Done');
      setInsightLoading(false);
      setTimeout(() => { setProgress(0); setStage(''); }, 900);
    }
  };

  const tone = (p) => (p >= 100 ? T.good : p > 0 ? T.warn : T.bad);

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" data-testid="module-drilldown-drawer">
      <div className="absolute inset-0" style={{ background: 'rgba(3,7,18,0.55)', backdropFilter: 'blur(3px)' }}
           onClick={onClose} data-testid="drilldown-backdrop" />
      <aside className="relative h-full w-full max-w-[620px] overflow-y-auto"
             style={{ background: T.card, borderLeft: `1px solid ${T.border}` }}>
        <header className="sticky top-0 z-10 px-5 py-3 flex items-center gap-3"
                style={{ background: T.card, borderBottom: `1px solid ${T.border}` }}>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] uppercase tracking-wider" style={{ color: T.muted }}>L2 · L3 Drilldown</div>
            <div className="text-sm font-semibold truncate" style={{ color: T.text }} data-testid="drilldown-title">
              {LABEL[module] || module}
            </div>
            {data?.totals && (
              <div className="text-[10px] font-mono" style={{ color: T.muted }}>
                {data.totals.items} items · {data.totals.groups} groups · avg {data.totals.avgProgress}%
              </div>
            )}
          </div>
          <button onClick={() => askCognara(openGroup)} disabled={insightLoading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold disabled:opacity-60"
            style={{ background: T.accent, color: '#fff' }} data-testid="drilldown-ask-cognara">
            {insightLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Ask Cognara
          </button>
          <button onClick={onClose} className="p-1 rounded hover:opacity-70" data-testid="drilldown-close">
            <X className="w-4 h-4" style={{ color: T.muted }} />
          </button>
        </header>

        {insightLoading && (
          <div className="mx-4 mt-4 rounded-xl p-3" data-testid="cognara-progress"
               style={{ background: T.inner, border: `1px solid ${T.accent}44` }}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 animate-pulse" style={{ color: T.accent }} />
              <span className="text-[11px] font-semibold" style={{ color: T.text }}>
                Cognara is analysing {openGroup || module}
              </span>
              <span className="ml-auto text-[11px] font-mono font-semibold" style={{ color: T.accent }}
                    data-testid="cognara-progress-pct">{progress}%</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: T.card }}>
              <div className="h-full rounded-full transition-all duration-700"
                   style={{ width: `${progress}%`,
                            background: `linear-gradient(90deg, ${T.accent}, ${T.violet})` }} />
            </div>
            <div className="text-[10px] mt-1.5" style={{ color: T.muted }} data-testid="cognara-progress-stage">{stage}</div>
          </div>
        )}

        {insight && (
          <div className="mx-4 mt-4 rounded-xl p-3 space-y-2" data-testid="drilldown-insight"
               style={{ background: T.inner, border: `1px solid ${T.accent}44` }}>
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" style={{ color: T.accent }} />
              <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: T.accent }}>
                Cognara · {insight.scope || module}
              </span>
            </div>
            {insight.status === 'ok' ? (
              <>
                <p className="text-[11px] font-medium" style={{ color: T.text }}>{insight.headline}</p>
                {(insight.findings || []).length > 0 && (
                  <ul className="space-y-1">
                    {insight.findings.map((f, i) => (
                      <li key={i} className="text-[11px] pl-2.5 border-l-2" style={{ color: T.text2, borderColor: T.accent }}>{f}</li>
                    ))}
                  </ul>
                )}
                {[['Delay analysis', insight.delayAnalysis, T.warn],
                  ['Impact of delay', insight.impact, T.bad],
                  ['Root cause (RCA)', insight.rca, T.violet]].map(([label, items, c]) => (
                  (items || []).length > 0 && (
                    <div key={label} className="space-y-1">
                      <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: c }}>{label}</div>
                      {items.map((x, i) => (
                        <div key={i} className="text-[11px] pl-2.5 border-l-2" style={{ color: T.text2, borderColor: c }}>{x}</div>
                      ))}
                    </div>
                  )
                ))}
                {(insight.risks || []).length > 0 && (
                  <div className="space-y-1">
                    {insight.risks.map((r, i) => (
                      <div key={i} className="flex gap-1.5 text-[11px]" style={{ color: T.text2 }}>
                        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" style={{ color: T.warn }} />{r}
                      </div>
                    ))}
                  </div>
                )}
                {(insight.actions || []).length > 0 && (
                  <div className="space-y-1">
                    {insight.actions.map((a, i) => (
                      <div key={i} className="flex gap-1.5 text-[11px]" style={{ color: T.text2 }}>
                        <Wrench className="w-3 h-3 mt-0.5 shrink-0" style={{ color: T.good }} />{a}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-[11px]" style={{ color: T.muted }}>{insight.message}</p>
            )}
          </div>
        )}

        {loading ? (
          <div className="py-20 flex items-center justify-center gap-2 text-xs" style={{ color: T.muted }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading drilldown…
          </div>
        ) : !data || (data.groups || []).length === 0 ? (
          <div className="py-20 text-center text-xs" style={{ color: T.muted }}>No data in this module yet.</div>
        ) : (
          <div className="p-4 space-y-2">
            {data.groups.map((g, gi) => {
              const open = openGroup === g.group;
              return (
                <motion.div key={g.group} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(gi, 8) * 0.03 }}
                  className="rounded-xl overflow-hidden"
                  style={{ background: T.inner, border: `1px solid ${open ? T.accent : T.border}` }}>
                  <button type="button" onClick={() => setOpenGroup(open ? null : g.group)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                    data-testid={`drilldown-group-${g.group.replace(/\s+/g, '-').toLowerCase()}`}>
                    <ChevronRight className="w-3.5 h-3.5 transition-transform shrink-0"
                      style={{ color: T.muted, transform: open ? 'rotate(90deg)' : 'none' }} />
                    <span className="flex-1 truncate text-[12px] font-medium" style={{ color: T.text }}>{g.group}</span>
                    <span className="text-[10px] font-mono" style={{ color: T.muted }}>{g.count} items</span>
                    <span className="text-[10px] font-mono" style={{ color: T.muted }}>{g.complete} done</span>
                    <span className="text-xs font-mono font-semibold w-12 text-right" style={{ color: tone(g.avgProgress) }}>
                      {g.avgProgress}%
                    </span>
                  </button>
                  <div className="h-1 mx-3 mb-2 rounded-full overflow-hidden" style={{ background: T.card }}>
                    <div className="h-full rounded-full transition-all duration-700"
                         style={{ width: `${Math.min(100, g.avgProgress)}%`, background: tone(g.avgProgress) }} />
                  </div>
                  {open && (
                    <div className="px-3 pb-3 space-y-0.5" data-testid="drilldown-l3-items">
                      {(g.items || []).length === 0 ? (
                        <div className="text-[11px] py-2" style={{ color: T.muted }}>No line items returned.</div>
                      ) : g.items.map((it, i) => (
                        <div key={`${it.ref}-${i}`} className="flex items-center gap-2 text-[10.5px] py-1.5"
                             style={{ borderBottom: `1px solid ${T.border}` }}>
                          <span className="font-mono shrink-0" style={{ color: T.muted, minWidth: 92 }}>{it.ref}</span>
                          <span className="flex-1 truncate" style={{ color: T.text2 }} title={it.name}>{it.name}</span>
                          {it.owner && <span className="hidden md:inline truncate max-w-[110px]" style={{ color: T.muted }}>{it.owner}</span>}
                          {it.slipDays != null && (
                            <span className="font-mono" style={{ color: it.slipDays > 0 ? T.bad : T.good }}>
                              {it.slipDays > 0 ? `+${it.slipDays}d` : 'on time'}
                            </span>
                          )}
                          {it.utilisationPct != null && (
                            <span className="font-mono" style={{ color: it.utilisationPct > 100 ? T.bad : T.muted }}>{it.utilisationPct}%</span>
                          )}
                          <span className="font-mono" style={{ color: T.muted }}>{it.due || '—'}</span>
                          <span className="font-mono font-semibold w-11 text-right" style={{ color: tone(it.progress) }}>{it.progress}%</span>
                        </div>
                      ))}
                      <button type="button" onClick={() => askCognara(g.group)} disabled={insightLoading}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-semibold"
                        style={{ background: `${T.accent}18`, color: T.accent, border: `1px solid ${T.accent}44` }}
                        data-testid={`drilldown-insight-${g.group.replace(/\s+/g, '-').toLowerCase()}`}>
                        <Sparkles className="w-3 h-3" /> Cognara insight for {g.group}
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </aside>
    </div>
  );
};

export default ModuleDrilldownDrawer;
